import { useState, useRef } from "react";
import { activityStore } from "../utils/dataStore";
import type { Vote, VoteOption, VoteTargetScope } from "../../../api/types";
import { apiDateToDatetimeLocalValue } from "../../../api/loadDataStore";
import ImageLibraryPicker from "../../../components/feature/ImageLibraryPicker";
import { uploadTenantImageFile } from "../../../api/tenantImageUpload";
import { useAdminData } from "../AdminDataContext";
import { countForTargetGroup } from "../utils/memberAudienceStatsUi";
import type { MemberAudienceUiCounts } from "../utils/memberAudienceStatsUi";
import { reportError } from "../../../api/errors";

interface NewOptionDraft {
  text: string;
  description: string;
  imageUrl: string;
  expanded: boolean;
}

interface TargetScopeDraft {
  mode: "all" | "level" | "activity";
  level: "level_normal" | "level_premium" | "level_director";
  activityId: string;
  activityRelation: "enrolled" | "not_enrolled";
}

interface NewVoteDraft {
  title: string;
  description: string;
  coverUrl: string;
  startDate: string;
  endDate: string;
  allowMultiple: boolean;
  isAnonymous: boolean;
  options: NewOptionDraft[];
  targetScope: TargetScopeDraft;
  participationPoints: number;
}

const emptyOption = (): NewOptionDraft => ({ text: "", description: "", imageUrl: "", expanded: false });

const defaultScope = (): TargetScopeDraft => ({
  mode: "all",
  level: "level_normal",
  activityId: "",
  activityRelation: "enrolled",
});

const emptyDraft = (): NewVoteDraft => ({
  title: "", description: "", coverUrl: "", startDate: "", endDate: "",
  allowMultiple: false, isAnonymous: true,
  options: [emptyOption(), emptyOption()],
  targetScope: defaultScope(),
  /** 与签到等场景一致：默认给予参与奖励，管理员可改为 0 表示不设奖励 */
  participationPoints: 5,
});

const draftFromVote = (vote: Vote): NewVoteDraft => ({
  title: vote.title,
  description: vote.description,
  coverUrl: (vote as Vote & { coverUrl?: string }).coverUrl ?? "",
  startDate: apiDateToDatetimeLocalValue(vote.startDate),
  endDate: apiDateToDatetimeLocalValue(vote.endDate),
  allowMultiple: vote.allowMultiple,
  isAnonymous: vote.isAnonymous,
  participationPoints: vote.participationPoints ?? 0,
  options: vote.options.map((o) => ({
    text: o.text,
    description: o.description || "",
    imageUrl: o.imageUrl || "",
    expanded: !!(o.imageUrl || o.description),
  })),
  targetScope: vote.targetScope ? {
    mode: vote.targetScope.type === "all" ? "all" :
      (vote.targetScope.type.startsWith("level_") ? "level" : "activity"),
    level: (vote.targetScope.type.startsWith("level_") ? vote.targetScope.type : "level_normal") as TargetScopeDraft["level"],
    activityId: vote.targetScope.activityId || "",
    activityRelation: vote.targetScope.type === "activity_not_enrolled" ? "not_enrolled" : "enrolled",
  } : defaultScope(),
});

const levelMeta = [
  { key: "level_normal" as const,   label: "普通会员", icon: "ri-shield-line",     color: "bg-gray-100 text-gray-600" },
  { key: "level_premium" as const,  label: "高级会员", icon: "ri-medal-line",      color: "bg-amber-50 text-amber-600" },
  { key: "level_director" as const, label: "理事会员", icon: "ri-vip-crown-line",  color: "bg-teal-50 text-teal-600" },
];

interface Props {
  onSave: (vote: Vote) => void;
  onClose: () => void;
  initialVote?: Vote;
  audience: MemberAudienceUiCounts | null;
  audienceLoading?: boolean;
}

export default function VoteCreateModal({ onSave, onClose, initialVote, audience, audienceLoading }: Props) {
  const { tenantLegacyId } = useAdminData();
  const isEdit = !!initialVote;
  const [draft, setDraft] = useState<NewVoteDraft>(() => initialVote ? draftFromVote(initialVote) : emptyDraft());
  const [imgErrors, setImgErrors] = useState<Record<number, boolean>>({});
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const coverFileRef = useRef<HTMLInputElement>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const dragIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const activities = activityStore.getAll().filter((a) => a.status === "ongoing" || a.status === "full");

  const setField = <K extends keyof NewVoteDraft>(k: K, v: NewVoteDraft[K]) =>
    setDraft((p) => ({ ...p, [k]: v }));

  const setScope = (patch: Partial<TargetScopeDraft>) =>
    setDraft((p) => ({ ...p, targetScope: { ...p.targetScope, ...patch } }));

  const setOption = (idx: number, patch: Partial<NewOptionDraft>) =>
    setDraft((p) => ({ ...p, options: p.options.map((o, i) => i === idx ? { ...o, ...patch } : o) }));

  const addOption = () => setDraft((p) => ({ ...p, options: [...p.options, emptyOption()] }));

  const removeOption = (idx: number) => {
    if (draft.options.length <= 2) return;
    setDraft((p) => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));
    setImgErrors((p) => { const next = { ...p }; delete next[idx]; return next; });
  };

  const toggleExpand = (idx: number) => setOption(idx, { expanded: !draft.options[idx].expanded });

  const handleFileChange = (idx: number, file: File | null) => {
    if (!file) return;
    void (async () => {
      try {
        const url = await uploadTenantImageFile(file, tenantLegacyId);
        setOption(idx, { imageUrl: url });
        setImgErrors((p) => ({ ...p, [idx]: false }));
      } catch (e) {
        reportError(e, "选项图上传失败");
      }
    })();
  };

  const handleDragStart = (idx: number) => { dragIdx.current = idx; };
  const handleDragOver = (e: React.DragEvent, idx: number) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === targetIdx) { dragIdx.current = null; setDragOverIdx(null); return; }
    setDraft((p) => {
      const opts = [...p.options];
      const [moved] = opts.splice(from, 1);
      opts.splice(targetIdx, 0, moved);
      return { ...p, options: opts };
    });
    dragIdx.current = null;
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { dragIdx.current = null; setDragOverIdx(null); };

  const canCreate = draft.title.trim() !== "" && draft.startDate !== "" && draft.endDate !== "" &&
    draft.options.filter((o) => o.text.trim()).length >= 2 &&
    (draft.targetScope.mode !== "activity" || draft.targetScope.activityId !== "");

  const filledOptions = draft.options.filter((o) => o.text.trim());
  const hasRichOptions = filledOptions.some((o) => o.imageUrl || o.description);

  const buildTargetScope = (): VoteTargetScope | undefined => {
    const { mode, level, activityId, activityRelation } = draft.targetScope;
    if (mode === "all") return undefined;
    if (mode === "level") return { type: level };
    const act = activities.find((a) => a.id === activityId);
    return {
      type: activityRelation === "enrolled" ? "activity_enrolled" : "activity_not_enrolled",
      activityId,
      activityTitle: act?.title,
    };
  };

  const handleSave = () => {
    if (!canCreate) return;
    const options: VoteOption[] = filledOptions.map((o, i) => ({
      id: isEdit ? (initialVote!.options[i]?.id ?? `o${i}`) : `o${i}`,
      text: o.text.trim(),
      votes: isEdit ? (initialVote!.options[i]?.votes ?? 0) : 0,
      ...(o.imageUrl ? { imageUrl: o.imageUrl } : {}),
      ...(o.description.trim() ? { description: o.description.trim() } : {}),
    }));
    const vote: Vote = {
      id: isEdit ? initialVote!.id : `v${Date.now()}`,
      title: draft.title.trim(),
      description: draft.description.trim(),
      options,
      status: "draft",
      startDate: draft.startDate,
      endDate: draft.endDate,
      totalVoters: isEdit ? initialVote!.totalVoters : 0,
      allowMultiple: draft.allowMultiple,
      isAnonymous: draft.isAnonymous,
      showToMembers: isEdit ? initialVote!.showToMembers : true,
      participants: isEdit ? initialVote!.participants : [],
      targetScope: buildTargetScope(),
      participationPoints: draft.participationPoints,
      ...((draft.coverUrl) ? { coverUrl: draft.coverUrl } : {}),
    } as Vote & { coverUrl?: string };
    onSave(vote);
  };

  const selectedActivity = activities.find((a) => a.id === draft.targetScope.activityId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-[640px] max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="font-bold text-gray-800 text-base">{isEdit ? "编辑投票" : "新建投票"}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{isEdit ? "修改草稿内容，选项图片和描述同步保留" : "支持图文选项、参与范围设置，让投票更精准"}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Cover Image Section ── */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5">
              <i className="ri-image-2-line text-red-500" />投票封面图
              <span className="text-gray-400 font-normal">（将显示在投票卡片顶部）</span>
            </label>
            {draft.coverUrl ? (
              <div className="relative rounded-xl overflow-hidden mb-2" style={{ height: 100 }}>
                <img src={draft.coverUrl} alt="封面预览" className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => setShowCoverPicker(true)}
                    className="bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer mr-2 whitespace-nowrap"
                  >
                    更换封面
                  </button>
                  <button
                    type="button"
                    onClick={() => setField("coverUrl", "")}
                    className="bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    移除封面
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowCoverPicker(true)}
                className="w-full h-20 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-3 text-gray-400 hover:border-red-300 hover:text-red-400 cursor-pointer transition-colors"
              >
                <i className="ri-image-add-line text-2xl" />
                <div className="text-left">
                  <p className="text-sm font-medium">选择封面图</p>
                  <p className="text-xs text-gray-300 mt-0.5">本地上传、资源库或自定义链接</p>
                </div>
              </button>
            )}
            {showCoverPicker && (
              <div className="border border-gray-200 rounded-xl overflow-hidden mt-2 bg-white">
                <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700">配置封面</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => coverFileRef.current?.click()}
                      className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 cursor-pointer bg-green-50 px-2 py-1 rounded-lg whitespace-nowrap"
                    >
                      <i className="ri-upload-line text-xs"></i> 本地上传
                    </button>
                    <input
                      ref={coverFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        void (async () => {
                          try {
                            const url = await uploadTenantImageFile(file, tenantLegacyId);
                            setField("coverUrl", url);
                            setShowCoverPicker(false);
                          } catch (err) {
                            reportError(err, "封面上传失败");
                          }
                          if (coverFileRef.current) coverFileRef.current.value = "";
                        })();
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => { setShowCoverPicker(false); setShowLibraryPicker(true); }}
                      className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 cursor-pointer bg-amber-50 px-2 py-1 rounded-lg whitespace-nowrap"
                    >
                      <i className="ri-image-2-line text-xs"></i> 资源库
                    </button>
                    <button type="button" onClick={() => setShowCoverPicker(false)} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                </div>
                <div className="px-3 py-3">
                  <p className="text-xs text-gray-500 mb-2">请使用上方「本地上传」「资源库」，或填写下方图片链接。</p>
                </div>
                <div className="px-3 pb-3">
                  <p className="text-xs text-gray-400 mb-1.5">或输入自定义图片链接：</p>
                  <input
                    type="url"
                    placeholder="https://..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-red-400"
                    onBlur={(e) => { if (e.target.value.startsWith("http")) { setField("coverUrl", e.target.value); setShowCoverPicker(false); } }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">投票标题 *</label>
            <input type="text" value={draft.title} onChange={(e) => setField("title", e.target.value)} placeholder="请输入投票主题" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">投票说明</label>
            <textarea value={draft.description} onChange={(e) => setField("description", e.target.value)} placeholder="请输入投票说明（可选）" rows={2} maxLength={300} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors resize-none" />
          </div>

          {/* Dates：精确到分钟 */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">投票开始时间 *</label>
              <input
                type="datetime-local"
                value={draft.startDate}
                onChange={(e) => setField("startDate", e.target.value)}
                className="w-full min-w-0 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">投票结束时间 *</label>
              <input
                type="datetime-local"
                value={draft.endDate}
                onChange={(e) => setField("endDate", e.target.value)}
                className="w-full min-w-0 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer"
              />
            </div>
          </div>

          {/* Settings */}
          <div className="flex items-center gap-6 bg-gray-50 rounded-xl px-4 py-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={draft.allowMultiple} onChange={(e) => setField("allowMultiple", e.target.checked)} className="w-4 h-4 rounded accent-red-600" />
              <span className="text-sm text-gray-600">允许多选</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={draft.isAnonymous} onChange={(e) => setField("isAnonymous", e.target.checked)} className="w-4 h-4 rounded accent-red-600" />
              <span className="text-sm text-gray-600">匿名投票</span>
            </label>
          </div>

          {/* Participation Points */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg">
                <i className="ri-coin-line text-amber-600 text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">参与积分奖励</p>
                <p className="text-xs text-gray-400 mt-0.5">会员完成投票后获得的积分数</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <input
                  type="number"
                  value={draft.participationPoints || ""}
                  onChange={(e) => setField("participationPoints", Math.max(0, Number(e.target.value)))}
                  placeholder="0（不设奖励）"
                  min={0}
                  max={9999}
                  className="w-full border border-amber-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white pr-14"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">积分</span>
              </div>
              {draft.participationPoints > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-amber-700 bg-amber-100 px-3 py-2 rounded-xl whitespace-nowrap flex-shrink-0">
                  <i className="ri-star-fill text-amber-500 text-xs"></i>
                  投票得 {draft.participationPoints} 积分
                </div>
              )}
            </div>
          </div>

          {/* ── Target Audience ── */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">
              参与人员范围
              <span className="ml-1.5 text-gray-400 font-normal">（限制哪些会员可以参与本次投票）</span>
            </label>
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              {/* Mode selector */}
              <div className="flex gap-1 p-2 bg-gray-50 border-b border-gray-100">
                {([
                  { key: "all",      label: "全体会员",   icon: "ri-group-line" },
                  { key: "level",    label: "按会员等级", icon: "ri-medal-line" },
                  { key: "activity", label: "按活动报名", icon: "ri-calendar-event-line" },
                ] as const).map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setScope({ mode: m.key })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${draft.targetScope.mode === m.key ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    <i className={m.icon}></i> {m.label}
                  </button>
                ))}
              </div>

              {/* All mode */}
              {draft.targetScope.mode === "all" && (
                <div className="px-4 py-3 flex items-center gap-3 text-sm text-gray-600">
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0">
                    <i className="ri-group-line text-gray-500"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">全体在册会员均可参与</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {audienceLoading || audience === null
                        ? "正在统计在册会员…"
                        : `当前共 ${audience.all.toLocaleString()} 名在册会员`}
                    </p>
                  </div>
                </div>
              )}

              {/* Level mode */}
              {draft.targetScope.mode === "level" && (
                <div className="px-4 py-3 space-y-2">
                  <p className="text-xs text-gray-400 mb-1">仅该等级会员可以参与投票：</p>
                  {levelMeta.map((lv) => {
                    const cnt =
                      audience === null || audienceLoading ? null : countForTargetGroup(lv.key, audience);
                    return (
                    <label key={lv.key} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${draft.targetScope.level === lv.key ? "border-teal-400 bg-teal-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                      <div className="flex items-center gap-2.5">
                        <input type="radio" checked={draft.targetScope.level === lv.key} onChange={() => setScope({ level: lv.key })} className="accent-teal-600" />
                        <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${lv.color}`}><i className={`${lv.icon} text-sm`}></i></div>
                        <span className="text-sm text-gray-700">{lv.label}</span>
                      </div>
                      <span className="text-xs text-gray-400">{cnt === null ? "…" : `${cnt} 人`}</span>
                    </label>
                    );
                  })}
                </div>
              )}

              {/* Activity mode */}
              {draft.targetScope.mode === "activity" && (
                <div className="px-4 py-3 space-y-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-2">选择关联活动（仅进行中）：</p>
                    {activities.length === 0 ? (
                      <div className="text-center py-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-gray-400">暂无进行中的活动</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                        {activities.map((act) => (
                          <label key={act.id} className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${draft.targetScope.activityId === act.id ? "border-teal-400 bg-teal-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <input type="radio" checked={draft.targetScope.activityId === act.id} onChange={() => setScope({ activityId: act.id })} className="accent-teal-600 flex-shrink-0" />
                              <span className="text-sm text-gray-700 truncate">{act.title}</span>
                            </div>
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{act.enrolled} 人</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-2">报名状态筛选：</p>
                    <div className="flex gap-2">
                      {(["enrolled", "not_enrolled"] as const).map((rel) => {
                        const count = selectedActivity
                          ? (rel === "enrolled" ? selectedActivity.enrolled : selectedActivity.capacity - selectedActivity.enrolled)
                          : 0;
                        return (
                          <button key={rel} onClick={() => setScope({ activityRelation: rel })}
                            className={`flex-1 py-2.5 rounded-xl text-sm border-2 cursor-pointer transition-all whitespace-nowrap ${draft.targetScope.activityRelation === rel ? "border-teal-400 bg-teal-50 text-teal-700 font-medium" : "border-gray-100 text-gray-600 hover:border-gray-200"}`}
                          >
                            <i className={`${rel === "enrolled" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} mr-1`}></i>
                            {rel === "enrolled" ? "已报名" : "未报名"}
                            {count > 0 && <span className="ml-1 text-xs opacity-70">({count})</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Options */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-gray-600">
                投票选项
                <span className="ml-1.5 text-gray-400 font-normal">（至少 2 个，可添加图片和描述）</span>
              </label>
              {hasRichOptions && (
                <span className="flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                  <i className="ri-image-line text-xs"></i> 图文模式
                </span>
              )}
            </div>

            <div className="space-y-3">
              {draft.options.map((opt, idx) => (
                <div key={idx} draggable onDragStart={() => handleDragStart(idx)} onDragOver={(e) => handleDragOver(e, idx)} onDrop={(e) => handleDrop(e, idx)} onDragEnd={handleDragEnd}
                  className={`border-2 rounded-2xl overflow-hidden transition-all ${dragOverIdx === idx && dragIdx.current !== idx ? "border-red-400 scale-[1.01]" : opt.expanded ? "border-red-200" : "border-gray-100"}`}
                >
                  <div className="flex items-center gap-3 px-4 py-3 bg-white">
                    <div className="w-5 flex flex-col items-center gap-0.5 cursor-grab active:cursor-grabbing flex-shrink-0">
                      <span className="w-3 h-0.5 bg-gray-300 rounded-full"></span>
                      <span className="w-3 h-0.5 bg-gray-300 rounded-full"></span>
                      <span className="w-3 h-0.5 bg-gray-300 rounded-full"></span>
                    </div>
                    <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-xs text-gray-500 flex-shrink-0 font-medium">{idx + 1}</span>
                    <input type="text" value={opt.text} onChange={(e) => setOption(idx, { text: e.target.value })} placeholder={`选项 ${idx + 1} 标题`} className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 transition-colors bg-white min-w-0" />
                    {opt.imageUrl && !imgErrors[idx] && (
                      <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                        <img src={opt.imageUrl} alt="" className="w-full h-full object-cover" onError={() => setImgErrors((p) => ({ ...p, [idx]: true }))} />
                      </div>
                    )}
                    <button type="button" onClick={() => toggleExpand(idx)} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors flex-shrink-0 ${opt.expanded ? "bg-red-50 text-red-500" : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"}`}>
                      <i className={`${opt.expanded ? "ri-image-fill" : "ri-image-add-line"} text-sm`}></i>
                    </button>
                    {draft.options.length > 2 && (
                      <button type="button" onClick={() => removeOption(idx)} className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-500 cursor-pointer rounded-lg hover:bg-red-50 transition-colors flex-shrink-0">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>

                  {opt.expanded && (
                    <div className="px-4 pb-4 bg-red-50/30 border-t border-red-100 space-y-3 pt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">选项图片</label>
                        <div className="flex gap-3">
                          <div className="w-28 h-20 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer hover:border-red-300 transition-colors bg-white" onClick={() => fileInputRefs.current[idx]?.click()}>
                            {opt.imageUrl && !imgErrors[idx] ? (
                              <img src={opt.imageUrl} alt="option" className="w-full h-full object-cover rounded-xl" onError={() => setImgErrors((p) => ({ ...p, [idx]: true }))} />
                            ) : (
                              <div className="text-center"><i className="ri-image-add-line text-2xl text-gray-300 block mb-1"></i><span className="text-xs text-gray-400">点击上传</span></div>
                            )}
                          </div>
                          <input ref={(el) => { fileInputRefs.current[idx] = el; }} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(idx, e.target.files?.[0] ?? null)} />
                          <div className="flex-1 flex flex-col gap-2">
                            <input type="url" value={opt.imageUrl.startsWith("data:") ? "" : opt.imageUrl}
                              onChange={(e) => { setOption(idx, { imageUrl: e.target.value }); setImgErrors((p) => ({ ...p, [idx]: false })); }}
                              placeholder="如：详细介绍此选项的背景、特点等…" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-red-400 bg-white" />
                            {opt.imageUrl && (
                              <button type="button" onClick={() => { setOption(idx, { imageUrl: "" }); setImgErrors((p) => ({ ...p, [idx]: false })); if (fileInputRefs.current[idx]) fileInputRefs.current[idx]!.value = ""; }} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 cursor-pointer w-fit">
                                <i className="ri-delete-bin-line text-xs"></i> 移除图片
                              </button>
                            )}
                            <p className="text-xs text-gray-400 leading-relaxed">建议比例 4:3，支持 JPG / PNG / GIF</p>
                          </div>
                        </div>
                        {imgErrors[idx] && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><i className="ri-error-warning-line"></i> 图片加载失败，请检查链接</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">选项描述<span className="ml-1 text-gray-400 font-normal">（向投票人展示更多信息）</span></label>
                        <textarea value={opt.description} onChange={(e) => setOption(idx, { description: e.target.value })} placeholder="如：详细介绍此选项的背景、特点等…" rows={2} maxLength={200} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-red-400 bg-white resize-none" />
                        <p className="text-xs text-gray-400 text-right mt-0.5">{opt.description.length}/200</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <button type="button" onClick={addOption} className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-600 cursor-pointer px-3 py-2 rounded-xl hover:bg-red-50 transition-colors w-full border-2 border-dashed border-red-200 justify-center">
                <i className="ri-add-line"></i> 添加选项
                {draft.options.length >= 6 && <span className="text-gray-400 ml-1">（最多 10 个）</span>}
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
              <i className="ri-information-line"></i>
              点击选项右侧 <i className="ri-image-add-line"></i> 图标可展开添加图片和描述
            </p>
          </div>

          {/* Preview */}
          {hasRichOptions && (
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-xs font-semibold text-gray-800 text-base">{isEdit ? "编辑投票" : "新建投票"}</p>
              <p className="text-xs text-gray-400 mt-0.5">{isEdit ? "修改草稿内容，选项图片和描述同步保留" : "支持图文选项、参与范围设置，让投票更精准"}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <p className="text-xs text-gray-400">
            已填写 <span className="font-semibold text-gray-700">{filledOptions.length}</span> 个有效选项
            {draft.targetScope.mode !== "all" && (
              <span className="ml-2 text-teal-600 font-medium">· 定向投票</span>
            )}
          </p>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-5 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 cursor-pointer whitespace-nowrap">取消</button>
            <button onClick={handleSave} disabled={!canCreate} className="px-6 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
              {isEdit ? "保存修改" : "保存草稿"}
            </button>
          </div>
        </div>
      </div>
      {showLibraryPicker && (
        <ImageLibraryPicker
          tenantLegacyId={tenantLegacyId}
          defaultCategory="vote"
          title="从资源库选择投票封面"
          onSelect={(img) => { setField("coverUrl", img.url); }}
          onClose={() => setShowLibraryPicker(false)}
        />
      )}
    </div>
  );
}
